import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  WrenchScrewdriverIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  LightBulbIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline'
import { PlayIcon, PlusIcon, MinusIcon, ArrowDownTrayIcon } from '@heroicons/react/24/solid'
import FlowForm from '../Elements/FlowForm'
import { soporte } from '../../Data/flows'

const serviciosData = [
  { key: 'reparacion', icon: WrenchScrewdriverIcon, image: '/repcampo.png' },
  { key: 'preventivo', icon: ArrowPathIcon, image: '/mantpreventivo.png' },
  { key: 'correctivo', icon: ShieldCheckIcon, image: '/mantcorrectivo.png' },
  { key: 'asesoria', icon: LightBulbIcon, image: '/asesoriatec.png' },
]

const categoriasVideos = [
  { key: 'todo' },
  { key: 'terminales' },
  { key: 'impresoras' },
  { key: 'lectores-rfid' },
  { key: 'consumibles' },
]

const videosData = [
  {
    key: 'impresoras-1',
    category: 'impresoras',
    duration: '4:12',
  },
  {
    key: 'terminales-1',
    category: 'terminales',
    duration: '3:05',
  },
  {
    key: 'lectores-rfid-1',
    category: 'lectores-rfid',
    duration: '6:40',
  },
  {
    key: 'terminales-2',
    category: 'terminales',
    duration: '2:48',
  },
  {
    key: 'impresoras-2',
    category: 'impresoras',
    duration: '4:55',
  },
  {
    key: 'consumibles-1',
    category: 'consumibles',
    duration: '5:18',
  },
]

const faqData = [
  { key: 'preventivo-frecuencia' },
  { key: 'lineas-blancas' },
  { key: 'termica-directa-transferencia' },
  { key: 'rfid-otro-pasillo' },
  { key: 'reparar-o-reemplazar' },
]

const descargablesData = [
  { key: 'guia-limpieza', file: 'guia-limpieza-cuidado-terminales.pdf' },
  { key: 'tabla-etiquetas', file: 'tabla-seleccion-etiquetas-ribbons.pdf' },
  { key: 'checklist-preventivo', file: 'checklist-preventivo-por-equipo.pdf' },
  { key: 'fichas-tecnicas', file: 'fichas-tecnicas-linea-distribuida.pdf' },
]

const polizasData = [
  { key: 'esencial', destacado: false, badge: false },
  { key: 'operativa', destacado: true, badge: true },
  { key: 'total', destacado: false, badge: false },
]

export default function SoportePage() {
  const { t } = useTranslation('soporte')
  const [servicioSeleccionado, setServicioSeleccionado] = useState('reparacion')
  const [categoriaVideoSeleccionada, setCategoriaVideoSeleccionada] = useState('todo')
  const [faqAbierta, setFaqAbierta] = useState(null)

  const videosFiltrados = videosData.filter(
    (video) => categoriaVideoSeleccionada === 'todo' || video.category === categoriaVideoSeleccionada
  )

  return (
    <div>
      {/* Hero section */}
      <div className="isolate relative flex min-h-screen flex-col overflow-hidden bg-gradient-to-br from-navy-950 via-navy-900 to-electric-700 pt-14">
        <div
          className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
          aria-hidden="true"
        >
          <div
            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[50rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-electric-400 to-neon-300 opacity-20 sm:left-[calc(50%-30rem)] sm:w-[100rem]"
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
          />
        </div>

        <div className="flex flex-1 items-center py-12">
          <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-6 lg:grid-cols-2 lg:px-8">
            {/* Columna izquierda */}
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white/80">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-neon-300 opacity-75"></span>
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-neon-300"></span>
                </span>
                {t('hero.eyebrow')}
              </span>

              <h1 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-5xl">
                {t('hero.titlePrefix')}{' '}
                <span className="text-neon-300">{t('hero.titleHighlight')}</span>
              </h1>

              <p className="mt-6 text-lg leading-8 text-white/70">
                {t('hero.description')}
              </p>

              <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {serviciosData.map((servicio) => {
                  const selected = servicio.key === servicioSeleccionado
                  return (
                    <button
                      key={servicio.key}
                      type="button"
                      onClick={() => setServicioSeleccionado(servicio.key)}
                      aria-pressed={selected}
                      className={`overflow-hidden rounded-xl bg-white text-left transition-all duration-200 ${
                        selected ? 'shadow-lg shadow-neon-300/30 ring-2 ring-neon-300' : 'ring-1 ring-white/10 hover:-translate-y-0.5 hover:shadow-lg'
                      }`}
                    >
                      <div className="flex h-24 items-center justify-center bg-gray-50 p-3">
                        {servicio.image ? (
                          <img
                            src={servicio.image}
                            alt=""
                            className="h-full w-full object-contain"
                            aria-hidden="true"
                          />
                        ) : (
                          <servicio.icon className="h-10 w-10 text-electric-600" aria-hidden="true" />
                        )}
                      </div>
                      <div className="p-4">
                        <p className="text-sm font-bold text-navy-900">
                          {t(`hero.servicios.${servicio.key}.title`)}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {t(`hero.servicios.${servicio.key}.description`)}
                        </p>
                      </div>
                    </button>
                  )
                })}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/80">
                <a href="tel:+528181294347" className="flex items-center gap-2 hover:text-white">
                  <PhoneIcon className="h-4 w-4 text-neon-300" aria-hidden="true" />
                  {t('hero.phone')}
                </a>
                <a href="https://wa.me/528118649258" className="flex items-center gap-2 hover:text-white">
                  <ChatBubbleLeftRightIcon className="h-4 w-4 text-neon-300" aria-hidden="true" />
                  {t('hero.whatsapp')}
                </a>
              </div>
            </div>

            {/* Columna derecha: formulario */}
            <div className="relative">
              <div
                className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-br from-electric-400/30 to-neon-300/20 blur-2xl"
                aria-hidden="true"
              />
              <FlowForm
                key={servicioSeleccionado}
                flow={soporte}
                preset={{ servicio: servicioSeleccionado }}
                compact
                className="relative !rounded-2xl !border-0 shadow-2xl"
              />
            </div>
          </div>
        </div>

        <svg
          className="pointer-events-none relative -mb-1 block h-12 w-full text-white sm:h-20"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,250.7C960,235,1056,181,1152,165.3C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          />
        </svg>
      </div>

      {/* Centro de recursos técnicos */}
      <div className="relative isolate overflow-hidden py-16">
        <div
          className="pointer-events-none absolute -right-24 top-0 -z-10 h-72 w-72 rounded-full bg-electric-100 opacity-50 blur-3xl"
          aria-hidden="true"
        />
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <h2 className="text-sm font-bold uppercase tracking-wide text-electric-600">
                {t('recursos.eyebrow')}
              </h2>
              <p className="mt-2 text-3xl font-bold tracking-tight text-navy-900 sm:text-4xl">
                {t('recursos.heading')}
              </p>
              <p className="mt-3 text-base leading-7 text-gray-600">
                {t('recursos.description')}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 lg:max-w-md lg:justify-end">
              {categoriasVideos.map((categoria) => {
                const selected = categoria.key === categoriaVideoSeleccionada
                return (
                  <button
                    key={categoria.key}
                    type="button"
                    onClick={() => setCategoriaVideoSeleccionada(categoria.key)}
                    aria-pressed={selected}
                    className={`rounded-full border px-4 py-2 text-xs font-semibold transition ${
                      selected
                        ? 'border-navy-900 bg-navy-900 text-white'
                        : 'border-gray-300 text-navy-900 hover:border-navy-400'
                    }`}
                  >
                    {t(`recursos.categorias.${categoria.key}`)}
                  </button>
                )
              })}
            </div>
          </div>

          <div
            className={
              categoriaVideoSeleccionada === 'todo'
                ? 'mt-10 flex gap-6 overflow-x-auto pb-4'
                : 'mx-auto mt-10 grid max-w-2xl grid-cols-1 gap-6 lg:max-w-none lg:grid-cols-3'
            }
          >
            {videosFiltrados.map((video) => (
              <div
                key={video.key}
                className={`group flex flex-col overflow-hidden rounded-2xl border border-gray-200 transition-all duration-200 hover:-translate-y-1 hover:border-electric-300 hover:shadow-xl hover:cursor-pointer ${
                  categoriaVideoSeleccionada === 'todo' ? 'w-72 flex-none' : ''
                }`}
              >
                <div className="relative flex h-40 items-center justify-center overflow-hidden bg-navy-900">
                  <p className="max-w-[80%] text-center text-sm text-white/10 transition-transform duration-300 group-hover:scale-105">{t(`recursos.videos.${video.key}.thumbnailLabel`)}</p>
                  <span className="absolute flex h-11 w-11 items-center justify-center rounded-full bg-neon-300 transition-transform duration-200 group-hover:scale-110">
                    <PlayIcon className="ml-0.5 h-4 w-4 text-navy-900" aria-hidden="true" />
                  </span>
                  <span className="absolute bottom-2 right-2 rounded bg-navy-950 px-2 py-1 text-xs font-semibold text-white">
                    {video.duration}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {t(`recursos.videos.${video.key}.categoryLabel`)}
                  </span>
                  <h3 className="mt-1.5 text-lg font-bold text-navy-900">{t(`recursos.videos.${video.key}.title`)}</h3>
                  <p className="mt-2 flex-auto text-sm leading-6 text-gray-600">{t(`recursos.videos.${video.key}.description`)}</p>
                  <button type="button" className="mt-3 inline-flex items-center text-sm font-semibold text-electric-600 hover:text-electric-700">
                    {t('recursos.verTutorial')}
                    <span aria-hidden="true" className="ml-1 inline-block transition-transform duration-200 group-hover:translate-x-1">&rarr;</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Preguntas frecuentes y descargables */}
      <div className="pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Preguntas técnicas frecuentes */}
            <div className="flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-6 sm:p-8">
              <h2 className="text-sm font-bold uppercase tracking-wide text-gray-400">
                {t('faqDescargables.faq.eyebrow')}
              </h2>
              <p className="mt-1.5 text-2xl font-bold tracking-tight text-navy-900">
                {t('faqDescargables.faq.heading')}
              </p>

              <div className="mt-4 divide-y divide-gray-200 border-t border-b border-gray-200">
                {faqData.map((item, index) => {
                  const abierta = index === faqAbierta
                  return (
                    <div key={item.key} className="relative">
                      <button
                        type="button"
                        onClick={() => setFaqAbierta(abierta ? null : index)}
                        aria-expanded={abierta}
                        className="flex w-full items-center justify-between gap-4 py-4 text-left"
                      >
                        <span className="text-sm font-semibold text-navy-900">
                          {t(`faqDescargables.faq.items.${item.key}.question`)}
                        </span>
                        {abierta ? (
                          <MinusIcon className="h-3.5 w-3.5 flex-none text-electric-600" aria-hidden="true" />
                        ) : (
                          <PlusIcon className="h-3.5 w-3.5 flex-none text-navy-900" aria-hidden="true" />
                        )}
                      </button>
                      {abierta && (
                        <div className="absolute inset-x-0 top-full z-10 rounded-b-xl border border-t-0 border-gray-200 bg-white p-4 shadow-xl">
                          <p className="text-sm leading-6 text-gray-600">
                            {t(`faqDescargables.faq.items.${item.key}.answer`)}
                          </p>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Descargables */}
            <div className="flex h-full flex-col rounded-2xl bg-slate-50 p-6 sm:p-8">
              <h2 className="text-sm font-bold uppercase tracking-wide text-gray-400">
                {t('faqDescargables.descargables.eyebrow')}
              </h2>
              <p className="mt-1.5 text-2xl font-bold tracking-tight text-navy-900">
                {t('faqDescargables.descargables.heading')}
              </p>

              <div className="mt-4 space-y-3">
                {descargablesData.map((item) => (
                  <a
                    key={item.key}
                    href={`/descargables/${item.file}`}
                    download
                    className="group flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 transition-all duration-200 hover:-translate-y-0.5 hover:border-electric-400 hover:shadow-md"
                  >
                    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-electric-50 text-electric-600 transition-colors duration-200 group-hover:bg-electric-600 group-hover:text-white">
                      <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block text-sm font-bold text-navy-900">
                        {t(`faqDescargables.descargables.items.${item.key}.title`)}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {t(`faqDescargables.descargables.items.${item.key}.meta`)}
                      </span>
                    </span>
                  </a>
                ))}
              </div>

              {/* <p className="mt-auto pt-5 text-sm text-gray-600">
                {t('faqDescargables.descargables.solicitarPrefix')}{' '}
                <a href="/contact" className="font-semibold text-electric-600 hover:text-electric-700">
                  {t('faqDescargables.descargables.solicitarCta')} <span aria-hidden="true">&rarr;</span>
                </a>
              </p> */}
            </div>
          </div>
        </div>
      </div>

      {/* Pólizas de servicio */}
      <div className="relative isolate overflow-hidden pb-24">
        <div
          className="pointer-events-none absolute -left-24 bottom-0 -z-10 h-72 w-72 rounded-full bg-neon-100 opacity-60 blur-3xl"
          aria-hidden="true"
        />
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <h2 className="text-sm font-bold uppercase tracking-wide text-electric-600">
                {t('polizas.eyebrow')}
              </h2>
              <p className="mt-2 text-3xl font-bold tracking-tight text-navy-900 sm:text-4xl">
                {t('polizas.heading')}
              </p>
              <p className="mt-4 text-base leading-7 text-gray-600">
                {t('polizas.description')}
              </p>
            </div>
            <a href="/contact" className="inline-flex items-center text-sm font-semibold text-electric-600 hover:text-electric-700 lg:mb-2">
              {t('polizas.pedirCenso')} <span aria-hidden="true" className="ml-1">&rarr;</span>
            </a>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
            {polizasData.map((poliza) => (
              <a
                key={poliza.key}
                // href="/contact"
                className={`group flex h-full flex-col rounded-2xl p-6 transition-all duration-200 sm:p-8 ${
                  poliza.destacado
                    ? 'bg-navy-900 shadow-lg lg:scale-105 '
                    : 'border border-gray-200 bg-white'
                }`}
              >
                <span className={`text-xs font-bold uppercase tracking-wide ${poliza.destacado ? 'text-neon-300' : 'text-electric-600'}`}>
                  {t(`polizas.items.${poliza.key}.nivel`)}
                  {poliza.badge ? ` · ${t(`polizas.items.${poliza.key}.badge`)}` : ''}
                </span>
                <h3 className={`mt-1.5 text-2xl font-bold ${poliza.destacado ? 'text-white' : 'text-navy-900'}`}>
                  {t(`polizas.items.${poliza.key}.title`)}
                </h3>
                <p className={`mt-2 text-sm leading-6 ${poliza.destacado ? 'text-white/70' : 'text-gray-600'}`}>
                  {t(`polizas.items.${poliza.key}.description`)}
                </p>

                <div className={`mt-4 border-t ${poliza.destacado ? 'border-white/10' : 'border-gray-200'}`} />

                <ul className="mt-4 mb-6 space-y-2">
                  {t(`polizas.items.${poliza.key}.features`, { returnObjects: true }).map((feature) => (
                    <li key={feature} className="flex items-center gap-2.5">
                      <PlusIcon className={`h-3.5 w-3.5 flex-none ${poliza.destacado ? 'text-neon-300' : 'text-electric-600'}`} aria-hidden="true" />
                      <span className={`text-sm ${poliza.destacado ? 'text-white' : 'text-navy-900'}`}>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* <span
                  className={`mt-auto flex items-center justify-center rounded-md px-5 py-3 text-center text-sm font-semibold transition-colors duration-200 ${
                    poliza.destacado
                      ? 'bg-neon-300 text-navy-900 group-hover:bg-neon-400'
                      : 'border border-gray-300 text-navy-900 group-hover:border-navy-400'
                  }`}
                >
                  {t(`polizas.items.${poliza.key}.cta`)}
                  <span aria-hidden="true" className="ml-1 inline-block transition-transform duration-200 group-hover:translate-x-1">&rarr;</span>
                </span> */}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Cierre */}
      <div className="pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-xl">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Panel oscuro */}
              <div className="relative isolate flex flex-col justify-center overflow-hidden bg-gradient-to-br from-navy-950 via-navy-900 to-electric-700 px-6 py-12 sm:px-10 sm:py-14">
                <div
                  className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-neon-300/20 blur-3xl"
                  aria-hidden="true"
                />
                <div className="relative">
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-neon-300">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-neon-300 opacity-75"></span>
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-neon-300"></span>
                    </span>
                    {t('cierre.badge')}
                  </span>
                  <p className="mt-4 text-2xl font-bold tracking-tight text-white sm:text-3xl">
                    {t('cierre.heading')}
                  </p>
                  <p className="mt-3 max-w-md text-sm leading-6 text-white/70">
                    {t('cierre.description')}
                  </p>
                  <div className="mt-6">
                    <Link
                      to="/contact"
                      className="flex w-full items-center justify-center rounded-md bg-neon-300 px-5 py-3 text-sm font-semibold text-navy-900 shadow-sm transition hover:bg-neon-400 lg:inline-flex lg:w-auto"
                    >
                      {t('cierre.cta')} <span aria-hidden="true" className="ml-1">&rarr;</span>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Panel de contacto directo */}
              <div className="flex flex-col justify-center gap-4 bg-gray-50 px-6 py-12 sm:px-10 sm:py-14">
                <a
                  href="tel:+528181294347"
                  className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 transition hover:border-electric-400"
                >
                  <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-electric-50 text-electric-600">
                    <PhoneIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-sm font-bold text-navy-900">{t('cierre.telefono.numero')}</span>
                    <span className="block text-xs text-gray-500">{t('cierre.telefono.label')}</span>
                  </span>
                </a>
                <a
                  href="https://wa.me/528118649258"
                  className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 transition hover:border-electric-400"
                >
                  <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-electric-50 text-electric-600">
                    <ChatBubbleLeftRightIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-sm font-bold text-navy-900">{t('cierre.whatsapp.label')}</span>
                    <span className="block text-xs text-gray-500">{t('cierre.whatsapp.sublabel')}</span>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
